// contentService.js - Unified client for D1 + R2 content persistence via the Worker API
// All user content (chats, images, videos, music) is stored in Cloudflare D1 + R2.
// Falls back silently when user is not authenticated.

const WORKER_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

function makeHeaders(userId) {
  return {
    'Content-Type': 'application/json',
    'x-user-id': userId,
  };
}

async function request(userId, path, options = {}) {
  if (!userId) return null;
  try {
    const res = await fetch(`${WORKER_URL}${path}`, {
      ...options,
      headers: {
        ...makeHeaders(userId),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      const err = await res.text();
      console.warn(`[contentService] ${options.method || 'GET'} ${path} → ${res.status}:`, err);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.warn('[contentService] Network error:', err);
    return null;
  }
}

/**
 * Poll a Kei.ai job until completed, failed, or timeout.
 * Returns the job object on completion, throws on failure/timeout.
 * @param {string} userId  - wallet address
 * @param {string} jobId   - Kei.ai taskId
 * @param {object} opts    - { intervalMs, maxAttempts, onProgress }
 */
export async function pollJob(userId, jobId, { intervalMs = 4000, maxAttempts = 90, onProgress } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    const data = await request(userId, `/api/jobs/${jobId}`);
    if (!data) {
      // Call onProgress with null so callers can count misses and fall back
      if (onProgress) onProgress(null, i);
      await new Promise(r => setTimeout(r, intervalMs));
      continue;
    }
    const job = data.job;
    if (onProgress) onProgress(job, i);
    if (job.status === 'completed') return job;
    if (job.status === 'failed') throw new Error(job.error || 'Generation failed');
    await new Promise(r => setTimeout(r, intervalMs));
  }
  throw new Error('Generation timed out - please check your history later');
}


// ── CHAT ───────────────────────────────────────────────────────────────────

export async function getChats(userId) {
  const data = await request(userId, '/api/content/chats');
  return data?.chats || [];
}

export async function createChat(userId, title, model) {
  const data = await request(userId, '/api/content/chats', {
    method: 'POST',
    body: JSON.stringify({ title, model }),
  });
  return data?.chatId || null;
}

export async function getChatMessages(userId, chatId) {
  const data = await request(userId, `/api/content/chats/${chatId}/messages`);
  return data?.messages || [];
}

export async function addMessage(userId, chatId, role, content) {
  const data = await request(userId, `/api/content/chats/${chatId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ role, content }),
  });
  return data?.messageId || null;
}

export async function updateChatTitle(userId, chatId, title) {
  await request(userId, `/api/content/chats/${chatId}/title`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

export async function deleteChat(userId, chatId) {
  await request(userId, `/api/content/chats/${chatId}`, { method: 'DELETE' });
}

// ── IMAGES ─────────────────────────────────────────────────────────────────

export async function saveImage(userId, { prompt, model, url, width, height, metadata }) {
  const data = await request(userId, '/api/content/images', {
    method: 'POST',
    body: JSON.stringify({ prompt, model, url, width, height, metadata }),
  });
  return data; // { success, id, publicUrl }
}

export async function getImages(userId) {
  const data = await request(userId, '/api/content/images');
  return data?.images || [];
}

export async function deleteImage(userId, id) {
  await request(userId, `/api/content/images/${id}`, { method: 'DELETE' });
}

// ── VIDEOS ─────────────────────────────────────────────────────────────────

export async function saveVideo(userId, { prompt, model, url, duration, metadata }) {
  const data = await request(userId, '/api/content/videos', {
    method: 'POST',
    body: JSON.stringify({ prompt, model, url, duration, metadata }),
  });
  return data; // { success, id, publicUrl }
}

export async function getVideos(userId) {
  const data = await request(userId, '/api/content/videos');
  return data?.videos || [];
}

// ── MUSIC ──────────────────────────────────────────────────────────────────

export async function saveMusic(userId, { prompt, model, url, duration, isInstrumental, metadata }) {
  const data = await request(userId, '/api/content/music', {
    method: 'POST',
    body: JSON.stringify({ prompt, model, url, duration, isInstrumental, metadata }),
  });
  return data; // { success, id, publicUrl }
}

export async function getMusic(userId, offset = 0, limit = 20) {
  const data = await request(userId, `/api/content/music?offset=${offset}&limit=${limit}`);
  return { tracks: data?.tracks || [], has_more: data?.has_more ?? false };
}


// ── SUMMARY ────────────────────────────────────────────────────────────────

export async function getContentSummary(userId) {
  const data = await request(userId, '/api/content/summary');
  return data?.summary || { chats: 0, images: 0, videos: 0, music: 0 };
}
